import React, { useState, useEffect } from "react";
import { createLead, updateLead, fetchLeadById } from "../api/leadService";
import { fetchContacts, createContact } from "../api/contactService";
import { fetchUsers } from "../api/userService";
import { fetchBranches } from "../api/branchService";
import { fetchLeadSources } from "../api/sourceService";
import { fetchServiceCategories } from "../api/serviceCategoryService";
import { calculatePriority } from "../utils/priorityCalculator";
import { fetchLeadStatuses } from "../api/statusService";
import UserSearchDropdown from "../components/UserSearchDropdown"; // Import the reusable component
import ContactSearchModal from "../components/ContactSearchModal"; // Import the reusable component
import { X } from "lucide-react"; // ✅ Import Lucide React close icon

function AddEditLeadModal({ isOpen, onClose, isEditing, leadId }) {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "", // ✅ Added email field
    alternate_phone: "",
    organization_id: "",
    branch_id: "",
    assigned_to: "",
    status_id: "",
    lead_source_id: "",
    lead_source_name: "", // ✅ Added lead_source_name to initial state
    address: "",
    city: "",
    notes: "",
    social_ids: {
      instagram: "",
      facebook: "",
      twitter: ""
    },
    inquiry_date: "",
    service_category_id: "",
    priority: "Low",
    contact_id: ""
  });

  // API Data States
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [sources, setSources] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  //const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const [showModal, setShowModal] = useState(false);

  // Filtered Lists
  const [filteredContacts, setFilteredContacts] = useState([]); // Holds search results for contacts
  const [filteredUsers, setFilteredUsers] = useState([]); // Holds search results for assigned users
  const [filteredCities, setFilteredCities] = useState([]);
  //const [filteredAssignedUsers, setFilteredAssignedUsers] = useState([]);
  const availableCities = ["Chennai", "Coimbatore", "Madurai", "Trichy", "Nagercoil", "Marthandam"];
  
  // Modal States
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", phone: "", location: "" });

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setShowModal(true), 100); // ✅ Small delay before rendering
      return () => clearTimeout(timer); // ✅ Cleanup function
    } else {
      setShowModal(false);
    }
  }, [isOpen]);
  // Fetch initial data
  useEffect(() => {
    if (!isOpen) return;
  
    let isMounted = true;
  
    const initializeData = async () => {
      try {
        setLoading(true);
  
        const [branchesRes, sourcesRes, categoriesRes, statusesRes] = await Promise.all([
          fetchBranches(),
          fetchLeadSources(),
          fetchServiceCategories(),
          fetchLeadStatuses()
        ]);
  
        if (!isMounted) return;
  
        setBranches(branchesRes);
        setSources(sourcesRes);
        setServiceCategories(categoriesRes);
        setStatuses(statusesRes);
  
        if (isEditing && leadId) {
          // ✅ If editing, load existing lead details
          const existingLead = await fetchLeadById(leadId);
          if (isMounted) {
            const assignedUser = existingLead.User; // ✅ Extract user details from API response
            setFormData(prev => ({
              ...prev,
              ...existingLead,
              assigned_to: assignedUser ? assignedUser.user_id : "", // ✅ Store User ID
              assigned_to_username: assignedUser 
              ? `${assignedUser.first_name} ${assignedUser.last_name}` 
              : "", // ✅ Store full name for display
              lead_source_id: existingLead.source_id || "",
              source: existingLead.source || "",
              priority: existingLead.priority || calculatePriority(existingLead)
            }));
          }
        } else {
          // ✅ If adding a new lead, set default values
          const todayDate = new Date().toISOString().split("T")[0]; // Format YYYY-MM-DD
          const defaultStatus = statusesRes.find(status => status.name.toLowerCase() === "new");
  
          setFormData(prev => ({
            ...prev,
            inquiry_date: todayDate, // ✅ Set today's date
            status_id: defaultStatus ? defaultStatus.lead_status_id : "" // ✅ Set "New" status if found
          }));
        }
      } catch (err) {
        if (isMounted) setError(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
  
    initializeData();
  
    return () => {
      isMounted = false;
    };
  }, [isOpen, isEditing, leadId]);
 
  useEffect(() => {
    if (!formData.lead_source_id || sources.length === 0) return;
  
    const selectedSource = sources.find(source => source.lead_source_id === formData.lead_source_id);
    if (selectedSource) {
      setFormData(prev => {
        if (prev.lead_source_name !== selectedSource.name.toLowerCase()) {
          return { ...prev, lead_source_name: selectedSource.name.toLowerCase() };
        }
        return prev; // ✅ Prevents unnecessary re-renders
      });
    }
  }, [formData.lead_source_id, sources]); // ✅ Ensures correct dependencies

  const handleChange = (e) => {
    const { name, value } = e.target;
  
    setFormData((prev) => {
      let updatedData = { 
        ...prev, 
        [name]: value,
        priority: (name === "lead_source_id" || name === "service_category_id") 
          ? calculatePriority({ ...prev, [name]: value }) 
          : prev.priority
      };
  
      // Handle social media separately
      if (name.startsWith("social_")) {
        const socialField = name.split("_")[1];
        return {
          ...prev,
          social_ids: { ...prev.social_ids, [socialField]: value }
        };
      }
  
      // ✅ Prevent execution if `sources` is empty
      if (name === "lead_source_id") {
        if (sources.length === 0) {
          console.warn("Skipping source update: Sources data not loaded yet.");
          return prev;
        }
  
        console.log("Finding Source in Sources List:", sources);  // ✅ Debug
        const selectedSource = sources.find((source) => source.lead_source_id === value);
        updatedData.lead_source_name = selectedSource ? selectedSource.name.toLowerCase() : "";
  
        // ✅ Reset contact fields if "Referral" is selected
        if (updatedData.lead_source_name === "referral") {
          updatedData.contact_id = "";
          updatedData.contact_name = "";
        }
      }
  
      console.log("Updated Data:", updatedData); // ✅ Debugging
      return updatedData;
    });
  };
  
  useEffect(() => {
    console.log("Lead Sources Loaded:", sources);
  }, [sources]);

  useEffect(() => {
    console.log("Updated Lead Source Name:", formData.lead_source_name);
  }, [formData.lead_source_name]);

  const handleContactSearch = async (e) => {
    const query = e.target.value;
    setFormData(prev => ({ ...prev, contact: query }));
  
    if (query.length > 2) {
      try {
        const response = await fetchContacts({ search: query });
        setFilteredContacts(response); // Store fetched contacts
      } catch (error) {
        console.error("Error fetching contacts:", error);
      }
    } else {
      setFilteredContacts([]);
    }
  };
// Add these functions in your component
const searchUsers = async (query) => {
  try {
    const response = await fetchUsers({ search: query });
    setFilteredUsers(response);
  } catch (error) {
    console.error("User search error:", error);
  }
};

 // Handle lead source change
 const handleSourceChange = (e) => {
  const { value } = e.target;
  setFormData((prev) => ({ ...prev, lead_source_id: value }));

  // If "Referral" is selected, clear previous contact selection
  if (value.toLowerCase() === "referral") {
    setFormData((prev) => ({ ...prev, contact_id: "", contact_name: "" }));
  }
};

const searchContacts = async (query) => {
  try {
    const response = await fetchContacts({ search: query });
    setFilteredContacts(response);
  } catch (error) {
    console.error("Contact search error:", error);
  }
};
const handleAssignedSearch = async (e) => {
  const query = e.target.value;
  setFormData(prev => ({ ...prev, assigned_to: query }));

  if (query.length > 2) {
    try {
      const response = await fetchUsers({ search: query });
      setFilteredUsers(response); // Store fetched users
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  } else {
    setFilteredUsers([]);
  }
};
const handleContactSelection = (contact) => {
  setFormData(prev => ({
    ...prev,
    contact_id: contact.contact_id, // Store selected contact ID
    contact: contact.name // Store name for display
  }));
  setFilteredContacts([]); // Clear dropdown
};
const handleUserSelection = (user) => {
  setFormData(prev => ({
    ...prev,
    assigned_to: user.username,
    assigned_to_id: user.user_id // Store user ID
  }));
  setFilteredUsers([]); // Clear dropdown
};
  const handleCitySearch = (e) => {
    const query = e.target.value.toLowerCase();
    const filtered = availableCities.filter(c => 
      c.toLowerCase().includes(query)
    );
    setFilteredCities(filtered);
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      console.log("Create Lead Data",formData);
      const payload = {
        ...formData,
        email: formData.email || null, // ✅ Added email
        lead_source_id: formData.lead_source_id || null,
        status_id: formData.status_id || null,  // ✅ Ensure status is passed correctly
        service_category_id: formData.service_category_id || null,
        contact_id: formData.contact_id || null,
        assigned_to: formData.assigned_to || null,
        branch_id: formData.branch_id || null
      };
      
      const response = isEditing 
        ? await updateLead(leadId, payload)
        : await createLead(payload);

        setSuccessMessage(isEditing ? "Lead updated successfully!" : "Lead created successfully!");

        // Hide success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage("");
          onClose(response);  // Pass the created/updated lead data here
          resetForm();
         // window.location.reload();
        }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      const newContactData = await createContact({
        name: newContact.name,
        phone: newContact.phone,
        address: newContact.location,
      });
  
      setFormData(prev => ({
        ...prev,
        contact_id: newContactData.contact_id,
        contact: newContactData.name
      }));
      
      setFilteredContacts([]); // Clear dropdown
      setIsContactModalOpen(false);
      setNewContact({ name: "", phone: "", location: "" });
    } catch (err) {
      console.error("Error adding contact:", err);
      setError(err.message);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      phone: "",
      alternate_phone: "",
      organization_id: "",
      branch_id: "",
      assigned_to: "",
      status: "New",
      source_id: "",
      address: "",
      city: "",
      notes: "",
      social_ids: { instagram: "", facebook: "", twitter: "" },
      inquiry_date: "",
      service_category_id: "",
      priority: "Low",
      contact_id: ""
    });
    setError(null);
  };
  const handleClose = () => {
    onClose(); // ✅ First, close the modal
  
    setTimeout(() => {
      resetForm(); // ✅ Reset form **only after modal is fully closed**
    }, 500); // Adjust delay if needed
  };
  if (!showModal) return null; // ✅ Prevents flickering by delaying render
  if (loading) return <div className="loading-indicator">Loading...</div>;
 
    return (
    
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity">
    <div className="bg-white w-full max-w-4xl h-[90vh] flex flex-col rounded-xl shadow-lg transform transition-all">      
      {successMessage && (
  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded m-4">
    {successMessage}
  </div>
)}
        {/* Modal Header */}
        <div className="px-6 py-4 border-b bg-gray-100 rounded-t-xl flex justify-between items-center">          <h2 className="text-xl font-semibold">
            {isEditing ? "Edit Lead" : "Add New Lead"}
          </h2>

        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded m-4">
            {error}
          </div>
        )}

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <form id="lead-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ">
          
              
              {/* Personal Information Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="mt-1 block w-full border rounded-md p-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="mt-1 block w-full border rounded-md p-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Email</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full border rounded-md p-2" required />
            </div>
            
              {/* Inquiry Date */}
              <div>
                <label htmlFor="inquiry_date" className="block text-sm font-medium text-gray-700">
                  Lead Creation Date
                </label>
                <input
                  type="date"
                  id="inquiry_date"
                  name="inquiry_date"
                  value={formData.inquiry_date}
                  onChange={handleChange}
                  className="mt-1 block w-full border rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Phone
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-1 block w-full border rounded-md p-2"
                />
              </div>
              <div className="col-span-1 md:col-span-2">
  <label className="block text-sm font-medium text-gray-700">
    Assigned To
  </label>
  <UserSearchDropdown
    value={formData.assigned_to_username} // Show username in UI
    onChange={(userId, username) =>
      setFormData((prev) => ({
        ...prev,
        assigned_to: userId, // Store user_id for backend
        assigned_to_username: username, // Store username for display
      }))
    }
  />
</div>
              {/* Company Information Section */}
              <div className="mb-4">
  <label className="block text-sm font-medium text-gray-700">Source</label>
  <select
    name="lead_source_id"
    value={formData.lead_source_id}
    onChange={handleChange}
    className="mt-1 block w-full border rounded-md p-2"
  >
    <option value="">Select Source</option>
    {sources.map((source) => (
      <option key={source.lead_source_id} value={source.lead_source_id}>
        {source.name}
      </option>
    ))}
  </select>
</div>

              {formData.lead_source_name && formData.lead_source_name.toLowerCase() === "referral" && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Contact
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.contact_name}
                  readOnly
                  placeholder="Select or create a contact"
                  className="border p-2 rounded w-full cursor-pointer"
                  onClick={() => setIsContactModalOpen(true)}
                />
                <button
                  type="button"
                  onClick={() => setIsContactModalOpen(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md"
                >
                  + Create Contact
                </button>
              </div>
            </div>
          )}
              {/* Contact Search (only if Source is Referral) */}
 {/* Contact Selection Modal */}
 {isContactModalOpen && (
  <ContactSearchModal
    isOpen={isContactModalOpen}
    onClose={() => setIsContactModalOpen(false)}
    onSelect={(contactId, contactName) =>
      setFormData((prev) => ({
        ...prev,
        contact_id: contactId,
        contact_name: contactName,
      }))
    }
  />
)}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Service Category
                </label>
                <select
                  name="service_category_id"
                  value={formData.service_category_id}
                  onChange={handleChange}
                  className="mt-1 block w-full border rounded-md p-2"
                >
                  <option value="">Select Category</option>
                  {serviceCategories.map(cat => (
                    <option key={cat.service_category_id} value={cat.service_category_id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Branch
                </label>
                <select
                  name="branch_id"
                  value={formData.branch_id}
                  onChange={handleChange}
                  className="mt-1 block w-full border rounded-md p-2"
                >
                  <option value="">Select Branch</option>
                  {branches.map(branch => (
                    <option key={branch.branch_id} value={branch.branch_id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                name="status_id"
                value={formData.status_id}
                onChange={handleChange}
                className="mt-1 block w-full border rounded-md p-2"
                >
                <option value="">Select Status</option>
                {statuses.map(status => (
                <option key={status.lead_status_id} value={status.lead_status_id}>
                {status.name}
                </option>
                ))}
                </select>
              </div>

              {/* Address Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="mt-1 block w-full border rounded-md p-2"
                />
              </div>
              {/* Location Information Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  City
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={(e) => {
                      handleChange(e);
                      handleCitySearch(e);
                    }}
                    className="mt-1 block w-full border rounded-md p-2"
                  />
                  {formData.city && filteredCities.length > 0 && (
                    <ul className="absolute z-10 bg-white border rounded-md shadow-lg mt-1 w-full">
                      {filteredCities.map(city => (
                        <li
                          key={city}
                          onClick={() => setFormData(prev => ({ ...prev, city }))}
                          className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                        >
                          {city}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Additional Fields Section */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="mt-1 block w-full border rounded-md p-2"
                  rows="3"
                ></textarea>
              </div>

              {/* Social Media Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Instagram ID
                </label>
                <input
                  type="text"
                  name="social_instagram"
                  value={formData.social_ids.instagram}
                  onChange={handleChange}
                  className="mt-1 block w-full border rounded-md p-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Facebook ID
                </label>
                <input
                  type="text"
                  name="social_facebook"
                  value={formData.social_ids.facebook}
                  onChange={handleChange}
                  className="mt-1 block w-full border rounded-md p-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Twitter ID
                </label>
                <input
                  type="text"
                  name="social_twitter"
                  value={formData.social_ids.twitter}
                  onChange={handleChange}
                  className="mt-1 block w-full border rounded-md p-2"
                />
              </div>
            </div>
          </form>
        </div>
            
            {/* Form Footer */}
            <div className="p-4 border-t bg-gray-100 flex justify-end space-x-3 rounded-b-xl">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-red-600 transition-colors"
              aria-label="Close"
              >
              <X size={24} />
              </button>
              <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg font-semibold shadow-md hover:bg-gray-300 transition duration-300 ease-in-out">
        Cancel
      </button>
              <button
               type="submit"
               form="lead-form"  // ✅ Ensures it targets the form
               className={`px-4 py-2 text-white bg-blue-600 rounded-lg font-semibold shadow-md transition duration-300 ease-in-out 
                hover:bg-blue-700 focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 
                ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={loading}
              >
              {loading ? "Saving..." : isEditing ? "Save Changes" : "Create Lead"}
              </button>
              </div>

</div>
</div>
);
}

        

export default React.memo(AddEditLeadModal);